import {UserEvent} from '../../models/user-event';
import {User} from '../../models/user';
import {ParisDatePipe} from '../../pipes/date/paris-date.pipe';

export interface JourneyStep {
  type: string;
  label: string;
  detail: string | null;
  start: Date;
  end: Date;
  count: number;
  failed: boolean;
  events: UserEvent[];
}

export interface JourneySession {
  user: User | null;
  start: Date;
  end: Date;
  ip: string | null;
  country: string | null;
  steps: JourneyStep[];
}

const SESSION_GAP_MS = 30 * 60 * 1000;
const BACKOFFICE_LOGIN_ACTION = 'User logged in';
const PERIOD_KEYS = ['startDate', 'endDate'];
const HIDDEN_KEYS = ['token', 'page', 'count', 'sort', 'order', 'groups', 'headers', 'withHeaders'];
const parisDate = new ParisDatePipe();

export function buildJourneys(events: UserEvent[]): JourneySession[] {
  const sessions: JourneySession[] = [];
  const openSessions = new Map<number | null, JourneySession>();

  const sorted = [...events].sort((a, b) => getTime(a) - getTime(b));

  for (const event of sorted) {
    const userId = event.user?.id ?? null;
    const time = new Date(getTime(event));
    let session = openSessions.get(userId);

    if (!session || isBackofficeLogin(event) || time.getTime() - session.end.getTime() > SESSION_GAP_MS) {
      session = {user: event.user, start: time, end: time, ip: event.ip, country: event.country, steps: []};
      sessions.push(session);
      openSessions.set(userId, session);
    }

    session.end = time;
    addStep(session, event, time);

    if (event.type === 'logout') {
      openSessions.delete(userId);
    }
  }

  return sessions.reverse();
}

export function describeFilters(filters: Record<string, any> | null | undefined, labels: Record<string, string> = {}): string | null {
  if (!filters || !Object.keys(filters).length) {
    return null;
  }

  const parts: string[] = [];

  if (PERIOD_KEYS.some(key => key in filters)) {
    const start = filters['startDate'] ? parisDate.transform(filters['startDate'], 'dd/MM/yyyy') : '…';
    const end = filters['endDate'] ? parisDate.transform(filters['endDate'], 'dd/MM/yyyy') : '…';
    parts.push('Period: ' + (start === end ? start : start + ' → ' + end));
  }

  for (const key of Object.keys(filters)) {
    if (PERIOD_KEYS.includes(key) || HIDDEN_KEYS.includes(key)) {
      continue;
    }

    const value = filters[key];
    const text = value === null ? 'none' : labels[key] ?? (Array.isArray(value) ? value.join(', ') : String(value));
    parts.push(humanizeKey(key) + ': ' + text);
  }

  return parts.join(' · ');
}

function addStep(session: JourneySession, event: UserEvent, time: Date): void {
  const label = getStepLabel(event);
  const previous = session.steps[session.steps.length - 1];

  if (previous && previous.type === event.type && previous.label === label) {
    previous.count++;
    previous.end = time;
    previous.events.push(event);
    return;
  }

  session.steps.push({
    type: event.type ?? 'other',
    label,
    detail: getStepDetail(event),
    start: time,
    end: time,
    count: 1,
    failed: (event.statusCode ?? 0) >= 400,
    events: [event],
  });
}

function getStepLabel(event: UserEvent): string {
  const data = event.data ?? {};

  switch (event.type) {
    case 'navigation':
      return data.title ?? event.path ?? 'Page';
    case 'filter':
      return 'Filter ' + (describeFilters(data.changes, data.labels) ?? '');
    case 'download':
      return 'Download ' + (data.filename ?? event.path ?? '');
    case 'logout':
      return 'Logout';
    case 'login':
      return isBackofficeLogin(event) ? 'Login' : event.action ?? 'Login';
    default:
      return event.action ?? event.path ?? '';
  }
}

function getStepDetail(event: UserEvent): string | null {
  const data = event.data ?? {};

  switch (event.type) {
    case 'navigation':
      return describeFilters(data.filters, data.labels);
    case 'download':
      return describeFilters(data.filters ?? data.query, data.labels);
    default:
      return null;
  }
}

function isBackofficeLogin(event: UserEvent): boolean {
  return event.type === 'login' && event.action === BACKOFFICE_LOGIN_ACTION;
}

function humanizeKey(key: string): string {
  const words = key
    .replace(/Ids?$/, '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase();

  return words.charAt(0).toUpperCase() + words.slice(1);
}

function getTime(event: UserEvent): number {
  return event.createdAt ? new Date(event.createdAt).getTime() : 0;
}
